/**
 * Thin GA4 wrapper — no NPM dependency, just calls `window.gtag(...)`
 * which is defined by the gtag.js snippet in public/index.html.
 *
 * SPA-safe: React Router doesn't reload the document, so we must fire
 * `page_view` manually on every route change (gtag() `config` is only called
 * once at boot with `send_page_view:false`).
 */

const GA_ID = "G-89MZB034WC";

const isGaReady = () => typeof window !== "undefined" && typeof window.gtag === "function";

/** Manually track a virtual page_view on SPA route changes. */
export function trackPageview(path) {
  if (!isGaReady()) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    send_to: GA_ID,
  });
}

/**
 * Fire a Google-recommended `generate_lead` event when an enquiry submits
 * OR when the sales team hits "Send quote" from the admin console. This
 * is the canonical lead-conversion event and can be imported into Google
 * Ads as a conversion action.
 */
export function trackLead({ source = "enquiry-form", productSku = null, value = 0 } = {}) {
  if (!isGaReady()) return;
  window.gtag("event", "generate_lead", {
    currency: "INR",
    value,
    source,
    ...(productSku ? { item_id: productSku } : {}),
    send_to: GA_ID,
  });
}

/**
 * Fire GA4's canonical `purchase` event when a Buy-Now CTA is clicked.
 *
 * NOTE: `Buy Now` in this codebase currently routes to a callback / quote
 * request flow rather than a live payment gateway, so this event captures
 * the "intent-to-purchase" — the strongest bottom-funnel signal we have
 * short of a real transaction. When Stripe is wired in later, keep this
 * firing but ALSO fire a second `purchase` with a real `transaction_id`
 * from the settlement callback.
 */
export function trackPurchase({ productSku = null, productName = "", value = 0, currency = "INR" } = {}) {
  if (!isGaReady()) return;
  // GA4 requires a stable transaction_id to de-dupe purchases; use a UUID
  // seeded with the current tick so the client can never mint two identical
  // ids in the same session.
  const txId = `buy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const item = {
    item_id: productSku || "unknown",
    item_name: productName || productSku || "unknown",
    quantity: 1,
    price: value || 0,
  };
  window.gtag("event", "purchase", {
    transaction_id: txId,
    value: value || 0,
    currency,
    items: [item],
    send_to: GA_ID,
  });
}

/**
 * Generic GA4 event helper — used by BuyNowButton for `begin_checkout` and
 * anywhere else we want to fire a named event without a dedicated helper.
 */
export function trackEvent(name, params = {}) {
  if (!isGaReady()) return;
  window.gtag("event", name, { send_to: GA_ID, ...params });
}

// ---------------------------------------------------------------------------
// Google Ads conversion tracking — AW-830781736
// ---------------------------------------------------------------------------
// Three conversion actions defined in Google Ads → Goals → Conversions:
//   * form_submit    — Contact / Enquiry form submitted (label live)
//   * phone_call     — Click on +91 85276 94100 anywhere on site (label live)
//   * quote_request  — Request Quote / Buy Now button clicked (label pending)
//
// Each action has a Google-issued LABEL (short opaque string) that we
// stitch to the Ads ID as `AW-830781736/{label}` in the `send_to`
// parameter of a `conversion` gtag event. The labels are fetched by the
// customer from Google Ads → Goals → Summary → Tag details → Event
// snippet, and pasted into ADS_LABELS below.
//
// While waiting for the quote_request label, ADS_LABELS keeps a
// `REPLACE_WITH_LABEL_QUOTE_REQUEST` placeholder — the helper no-ops
// silently until it's swapped for the real value so we never fire a
// broken conversion against Google's servers.

const ADS_CONVERSION_ID = "AW-830781736";

const ADS_LABELS = {
  // Contact / enquiry form submitted (live 25 Aug 2026)
  form_submit: "FNRWCOzYguccEKjykowD",
  // Phone-call click on +91 85276 94100 (live 25 Aug 2026)
  phone_call: "FNRWCOzYguccEKjykowD",
  // Quote / Buy Now click — label pending from Google Ads dashboard
  quote_request: "REPLACE_WITH_LABEL_QUOTE_REQUEST",
};

/** Internal — returns false and logs a debug line if the label is still a placeholder. */
function _fireAdsConversion(key, params = {}) {
  if (!isGaReady()) return false;
  const label = ADS_LABELS[key];
  if (!label || label.startsWith("REPLACE_WITH_LABEL_")) {
    // Non-fatal: log to console so devs know the label is pending. Keeps
    // production quiet by only logging when a devtools console is open.
    if (typeof window !== "undefined" && window.console && window.console.debug) {
      window.console.debug(`[ads] skipping conversion "${key}" — label pending`);
    }
    return false;
  }
  window.gtag("event", "conversion", {
    send_to: `${ADS_CONVERSION_ID}/${label}`,
    ...params,
  });
  return true;
}

/**
 * Fire the "Contact form submit" Ads conversion (Action ID 7732243564).
 * Call this in the same success handler as `trackLead()` — right after
 * the enquiry API confirms the submission.
 */
export function trackAdsFormSubmit(params = {}) {
  return _fireAdsConversion("form_submit", params);
}

/**
 * Fire the "Phone call click" Ads conversion (Action ID 7732243567).
 * Wired to a global click delegator (installed from App.js) that catches
 * every `a[href^="tel:"]` click site-wide — new phone links added later
 * are instrumented automatically.
 */
export function trackAdsPhoneCall(params = {}) {
  return _fireAdsConversion("phone_call", params);
}

/**
 * Fire the "Quote / Purchase request" Ads conversion (Action ID 7732243570).
 * Call from any "Request a Quote" / "Buy Now" click handler — usually
 * alongside `trackPurchase()` in BuyNowButton.jsx.
 */
export function trackAdsQuoteRequest(params = {}) {
  return _fireAdsConversion("quote_request", params);
}

// ---------------------------------------------------------------------------
// Phone-click delegator
// ---------------------------------------------------------------------------
// Installed once from App.js on mount. Uses event delegation on the
// document so every `<a href="tel:+918527694100">` (including future
// additions) fires the Ads phone conversion — zero per-component wiring.
export function installPhoneClickTracker() {
  if (typeof document === "undefined") return () => {};
  const handler = (e) => {
    // Only interested in left-click on anchor elements. Use the closest()
    // walk so clicks on nested spans / icons still resolve to the <a>.
    if (e.button !== 0) return;
    const anchor = e.target.closest && e.target.closest('a[href^="tel:"]');
    if (!anchor) return;
    trackAdsPhoneCall({ href: anchor.getAttribute("href") });
  };
  document.addEventListener("click", handler, true);
  return () => document.removeEventListener("click", handler, true);
}
